// The gateway's day-budget refusal, recognised by its own `budget` marker (the
// same string packages/gateway/src/app.ts emits and session/retry.ts matches)
// — never by `code`, which is `insufficient_quota` and therefore also what a
// BYOK OpenAI account says when it is genuinely out of credits.
const INSTALL_DAILY_BUDGET_MARKER = /"budget"\s*:\s*"gigatool_install_daily"/

export type DailyBudgetError = {
  // When the budget resets, epoch ms — absent when the body carries no usable
  // `reset_at` (the card then says "midnight UTC" and the app falls back to
  // its own reading of the limits).
  resetAt?: number
}

/**
 * Reads an SDK error's data as the gateway's daily-budget refusal. Returns
 * undefined when this is not that error, otherwise the absolute reset instant
 * the body carries (when it does): `reset_at` is absolute on purpose, so a
 * card rendered hours later still names the right time.
 */
export function dailyBudgetError(data: unknown): DailyBudgetError | undefined {
  const body = (data as { responseBody?: unknown } | undefined)?.responseBody
  if (typeof body !== "string" || !INSTALL_DAILY_BUDGET_MARKER.test(body)) return undefined
  try {
    const json = JSON.parse(body) as { error?: { reset_at?: unknown }; reset_at?: unknown }
    const at = json.error?.reset_at ?? json.reset_at
    if (typeof at === "string") {
      const ms = new Date(at).getTime()
      if (Number.isFinite(ms)) return { resetAt: ms }
    }
  } catch {
    // not JSON — still the budget error, just without a time
  }
  return {}
}

/** "HH:MM" in the given locale, for a reset instant. */
export function formatClock(at: number, locale?: string): string {
  return new Date(at).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })
}
